import { SkeletonLoader } from '@toggle/design-system';
import { EarningsInsight } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useGetEarningById } from '~/react-query-hooks/earnings/useGetEarningById';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { useFlags } from '~/stores/use-flags/useFlags';
import { EarningsInsightPill } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/components/earnings-insight-pill/EarningsInsightPill';
import { useEarningsInsightDrawer } from '~/widgets/earnings-insight-drawer/useEarningsInsightDrawer';

import { EarningsDateLabel } from '../../components/earnings-date-label/EarningsDateLabel';
import { PublishedDate } from '../../components/published-date/PublishedDate';
import { EarningsExpectationsTable } from './earnings-expectations-table/EarningsExpectationsTable';
import styles from './PreviewOverview.module.scss';
import { TargetPriceSection } from './target-price-section/TargetPriceSection';

export interface PreviewOverviewProps {
  title: string;
  activeEntityTag: string;
  publishedAt: string;
  previewEarningsInsight?: EarningsInsight;
}

export const PreviewOverview = ({
  title,
  activeEntityTag,
  publishedAt,
  previewEarningsInsight,
}: PreviewOverviewProps) => {
  const { t } = useTranslation(['earnings']);
  const { earningsId } = useEarningsInsightDrawer();
  const isUniversalDrawerEnabled = useFlags(state =>
    state.getFeatureFlagState('universal_drawer')
  );

  const {
    data: earningsData,
    isFetching,
    isError,
    error,
  } = useGetEarningById(earningsId ?? '');

  if (isFetching) {
    return (
      <div className={styles.loaderWrapper}>
        <SkeletonLoader
          areas={[
            {
              styles: {
                width: '100%',
                height: '100px',
                borderRadius: '8px',
              },
            },
          ]}
        />
      </div>
    );
  }

  if (isError) {
    throw new Error(error?.message);
  }

  return (
    <div className={styles.container}>
      <div className={styles.pillWrapper}>
        {previewEarningsInsight && isUniversalDrawerEnabled && (
          <EarningsInsightPill
            entityTag={activeEntityTag}
            earningsInsight={previewEarningsInsight}
            isClickable={false}
            isPreview
            source={POSTHOG_TRACKING_SOURCES.EARNINGS_INSIGHT_DRAWER}
          />
        )}
        <PublishedDate publishedAt={publishedAt} isPreview />
      </div>
      <p className={styles.title}>{title}</p>
      <div className={styles.wrapper}>
        {earningsData && (
          <>
            <EarningsDateLabel
              date={earningsData?.reporting_date}
              label={t(
                'earningsInsight.drawer.earningsPreview.earningsReleased'
              )}
            />
            <EarningsExpectationsTable earningsData={earningsData} />
          </>
        )}
        <ErrorBoundaryWrapper>
          <TargetPriceSection entityTag={activeEntityTag} />
        </ErrorBoundaryWrapper>
      </div>
    </div>
  );
};
